import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchUpcomingLaw, fetchUpcomingLaws } from "@/api/legislationMonitor";
import useDebounce from "@/hooks/useDebounce";
import { emptyPagination } from "@/lib/jsonPagination";
import type {
  MunicipalImplicationsStatus,
  StatutoryAnalysisStatus,
} from "@/types/legislationMonitor";

const SEARCH_DEBOUNCE_MS = 300;
const UPCOMING_LAWS_PER_PAGE = 10;
const ANALYSIS_POLL_INTERVAL_MS = 4000;
const ANALYSIS_POLL_MAX_INTERVAL_MS = 60_000;
/** ~5 min at 4s baseline before backoff cap; stops unbounded polling. */
const ANALYSIS_MAX_POLLS = 75;

type AnalysisStatus = StatutoryAnalysisStatus | MunicipalImplicationsStatus;

/** Poll while any analysis is in flight; exponential backoff capped at 60s. */
export function analysisRefetchInterval(
  statuses: ReadonlyArray<AnalysisStatus | undefined>,
  dataUpdateCount: number,
): number | false {
  const shouldPoll = statuses.some((status) => status === "pending" || status === "processing");
  if (!shouldPoll) {
    return false;
  }

  const pollsCompleted = Math.max(0, dataUpdateCount - 1);
  if (pollsCompleted >= ANALYSIS_MAX_POLLS) {
    return false;
  }

  const backoffStep = Math.min(pollsCompleted, 4);
  return Math.min(
    ANALYSIS_POLL_INTERVAL_MS * 2 ** backoffStep,
    ANALYSIS_POLL_MAX_INTERVAL_MS,
  );
}

class UpcomingLawsFetchError extends Error {
  constructor(readonly status: number) {
    super(`Upcoming laws request failed with status ${status}`);
    this.name = "UpcomingLawsFetchError";
  }
}

function upcomingLawsErrorMessage(error: unknown): string {
  if (error instanceof UpcomingLawsFetchError) {
    if (error.status === 403) {
      return "You do not have access to upcoming laws for this state.";
    }
    if (error.status === 400) {
      return "Select a state to view upcoming laws.";
    }
  }

  return "Unable to load upcoming laws. Try again.";
}

function upcomingLawDetailErrorMessage(error: unknown): string {
  if (error instanceof UpcomingLawsFetchError && error.status === 403) {
    return "You do not have access to this upcoming law.";
  }

  return "Unable to load this upcoming law.";
}

export function useUpcomingLawsSearch(state: string | null) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const queryDebounced = useDebounce(query, SEARCH_DEBOUNCE_MS);

  const [prevFilters, setPrevFilters] = useState({ state, queryDebounced });
  const stateChanged = state !== prevFilters.state;
  let queryDebouncedForQuery = queryDebounced;
  let pageForQuery = page;
  if (stateChanged) {
    queryDebouncedForQuery = "";
    if (query !== "") {
      setQuery("");
    }
  } else if (query === "" && prevFilters.queryDebounced === "") {
    queryDebouncedForQuery = "";
  }

  if (stateChanged || queryDebouncedForQuery !== prevFilters.queryDebounced) {
    setPrevFilters({ state, queryDebounced: queryDebouncedForQuery });
    pageForQuery = 1;
    if (page !== 1) {
      setPage(1);
    }
  }

  const listQuery = useQuery({
    queryKey: ["upcoming_laws", state, queryDebouncedForQuery, pageForQuery] as const,
    queryFn: async ({ signal }) => {
      const result = await fetchUpcomingLaws({
        state: state!,
        q: queryDebouncedForQuery,
        page: pageForQuery,
        perPage: UPCOMING_LAWS_PER_PAGE,
        signal,
      });

      if (!result.ok) {
        throw new UpcomingLawsFetchError(result.status);
      }

      return result.data;
    },
    enabled: state != null,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === state ? previous : undefined,
  });

  return {
    query,
    setQuery,
    page,
    setPage,
    laws: state != null ? (listQuery.data?.upcoming_laws ?? []) : [],
    pagination:
      state != null
        ? (listQuery.data?.pagination ?? emptyPagination(UPCOMING_LAWS_PER_PAGE))
        : emptyPagination(UPCOMING_LAWS_PER_PAGE),
    loading: state != null && listQuery.isFetching,
    error: listQuery.error ? upcomingLawsErrorMessage(listQuery.error) : null,
  };
}

export function useUpcomingLawDetail(lawId: number | null, municipalityId?: number | null) {
  const detailQuery = useQuery({
    queryKey: ["upcoming_law", lawId, municipalityId ?? null] as const,
    queryFn: async ({ signal }) => {
      const result = await fetchUpcomingLaw(lawId!, {
        municipalityId: municipalityId ?? undefined,
        signal,
      });

      if (!result.ok) {
        throw new UpcomingLawsFetchError(result.status);
      }

      return result.data;
    },
    enabled: lawId != null,
    refetchInterval: (query) =>
      analysisRefetchInterval(
        [
          query.state.data?.statutory_analysis?.status,
          query.state.data?.municipal_implications?.status,
        ],
        query.state.dataUpdateCount,
      ),
  });

  const hasCachedLaw = detailQuery.data?.upcoming_law != null;

  return {
    law: detailQuery.data?.upcoming_law ?? null,
    statutoryAnalysis: detailQuery.data?.statutory_analysis ?? null,
    municipalImplicationsEligible: detailQuery.data?.municipal_implications_eligible ?? false,
    municipalImplications: detailQuery.data?.municipal_implications ?? null,
    loading: detailQuery.isLoading,
    error:
      detailQuery.error && !hasCachedLaw
        ? upcomingLawDetailErrorMessage(detailQuery.error)
        : null,
  };
}
